import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Dialog, InputBase, alpha, styled } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { GridColumns, GridRenderCellParams, GridSelectionModel, GridSortModel, DataGridProProps, GRID_CHECKBOX_SELECTION_COL_DEF, GridOverlay } from '@mui/x-data-grid-pro';
import { getShortDateString, getTimeString } from '../../util/DateTimeUtility';
import GridCellDualVert from '../../components/listItems/GridCellDualVert';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import OpenIcon from '@mui/icons-material/OpenInBrowser';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ReprocessIcon from '@mui/icons-material/Repeat';
import { TabContainer, TabContent, TabToolbar, TabDataGrid } from '../../util/SharedStyles';
import DeleteDialog from '../dialogs/DeleteDialog';
import SelectDirectoryDialog from '../dialogs/SelectDirectoryDialog';
import ConfirmationPrompt from '../ConfirmationPrompt';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { RequestResult } from '../../gql-types.generated';
import { downloadFile, deleteFile, moveFile, reprocessFile, deleteDirectory } from '../../features/FileManager/FileManagerActions';
import { 
    selectDownloadFileStatus, 
    selectDeleteFileStatus, 
    selectMoveFileStatus,
    captureDownloadFileStatus, 
    captureDeleteFileStatus, 
    captureMoveFileStatus,
    selectError, 
    selectActionError,
    selectReprocessFileStatus,
    captureReprocessFileStatus,
    clearActionError,
    selectDeleteDirectoryStatus,
    captureDeleteDirectoryStatus
} from '../../features/FileManager/FileManagerSlice';
import { downloadDocument } from '../../util/Common';
import { setToastConfig } from '../../features/EDIContainer/EDIContainerSlice';
import { ToastSeverity, Viewer } from '../../util/Constants';
import ErrorMessage from '../ErrorMessage';
import NoRecordsMessage from '../NoRecordsMessage';
import NoResultsMessage from '../NoResultsMessage';
import { viewerIsAdmin } from '../../util/ViewerUtility';

const SearchContainer = styled('div')(({ theme }) => ({
    position: 'relative',
    backgroundColor: alpha(theme.palette.common.black, 0.05),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    width: '40%',
    height: '40px'
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
    width: '100%',
    height: '100%',
    padding: theme.spacing(1, 1, 1, 2),
    '& .MuiInputBase-input': {
        '&::placeholder': {
            color: theme.palette.text.primary,
            opacity: 0.8
        }
    }
}));

export enum FileSystemType {
    Directory = 1,  // Shows first in ASC, second in DESC
    File = 2       // Shows second in ASC, first in DESC
}

export interface FileRow {
    id: string;        // Path serves as ID
    name: string;      // File/directory name
    type: FileSystemType;  // Directory = 1, File = 2
    isDirectory: boolean;  // Flag for directory vs file
    path: string;     // Full path
    lastModifiedTime: string;  // Last modified timestamp in ISO format
    createdTime: string;  // Last modified timestamp in ISO format
    fileSizeDisplay?: string;  // Optional formatted file size for files
    fileSize?: number;  // File size in bytes, optional for directories
    isDeleteDisabled?: boolean;  // Optional flag for directories that can't be deleted
}

// Custom data grid component to allow for custom styles and behaviors
// This component can be extended or modified as needed for specific file management features
const FileManagerDataGrid: React.FC<DataGridProProps> = (props) => {
    return (
        <TabDataGrid
            {...props}
            sx={{
                '& .MuiDataGrid-row.directory-row': {
                    cursor: 'pointer'
                },
                '& .MuiDataGrid-row.file-row': {
                    cursor: 'default'
                },
                '& .MuiDataGrid-row.file-row:hover': {
                    backgroundColor: 'unset !important',
                    color: 'unset !important',
                    cursor: 'default'
                }
            }}
        />
    );
};

interface FileManagerTabBaseProps {
    // Base props
    viewer: Viewer | undefined;
    showFileActionButtons?: boolean;
    showReprocessButton?: boolean;
    onRefreshTab: () => void;
    onRefreshTree?: () => void;

    // Grid data
    rows: FileRow[];
    columns: GridColumns;
    defaultSortField?: string;
    defaultSortDirection?: 'asc' | 'desc';
    
    // Grid customization
    overlayErrorTitle?: string;
    noRowsMessage?: string;
    noResultsMessage?: string;
    loading?: boolean;
    checkboxSelection?: boolean;
    gridProps?: Partial<DataGridProProps>;
    checkboxColumnProps?: {
        renderCell?: (params: GridRenderCellParams) => React.ReactNode;
        width?: number;
    };

    // Toolbar content
    customToolbarBreadcrumbsContent?: React.ReactNode;
    customToolbarButtonContent?: React.ReactNode;

    // Search functionality (always enabled)
    searchPlaceholder?: string;
    onSearchChange?: (searchText: string) => void;
    onSearchSubmit?: (searchText: string) => void;
    searchValue?: string;
    minSearchLength?: number;
}

const FileManagerTabBase: React.FC<FileManagerTabBaseProps> = (props) => {
    const {
        viewer,
        customToolbarBreadcrumbsContent,
        customToolbarButtonContent,
        showFileActionButtons = false,
        showReprocessButton = true,
        columns,
        rows,
        defaultSortField = 'name',
        defaultSortDirection = 'asc',
        overlayErrorTitle: overLayErrorTitle,
        noRowsMessage,
        noResultsMessage,
        loading,
        checkboxSelection,
        gridProps,
        checkboxColumnProps,
        onRefreshTab: refreshTab,
        onRefreshTree: refreshTree,
        searchPlaceholder = 'Search...',
        onSearchChange,
        onSearchSubmit,
        searchValue = '',
        minSearchLength = 0
    } = props;

    const dispatch = useAppDispatch();
    const [selectedFiles, setSelectedFiles] = useState<FileRow[]>([]);
    const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([{
        field: defaultSortField,
        sort: defaultSortDirection
    }]);
    
    // User preference for page size with localStorage persistence
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem('fileManager-pageSize');
        return saved ? parseInt(saved, 10) : 50; // Default to 50 if not set
    });

    // Handle page size change and persist to localStorage
    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        localStorage.setItem('fileManager-pageSize', newPageSize.toString());
    }, []);
    
    const [isDownloading, setIsDownloading] = useState(false);
    const [shouldOpenInTab, setShouldOpenInTab] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
    const [openMoveDialog, setOpenMoveDialog] = useState(false);
    const [moveErrorMessage, setMoveErrorMessage] = useState('');
    const [openReprocessDialog, setOpenReprocessDialog] = useState(false);
    const [isReprocessing, setIsReprocessing] = useState(false);
    const [reprocessErrorMessage, setReprocessErrorMessage] = useState('');
    const [selectedDirectoryPath, setSelectedDirectoryPath] = useState<string>('');

    const downloadStatus = useAppSelector(selectDownloadFileStatus);
    const deleteDirectoryStatus = useAppSelector(selectDeleteDirectoryStatus);
    const deleteStatus = useAppSelector(selectDeleteFileStatus);
    const moveStatus = useAppSelector(selectMoveFileStatus);
    const reprocessStatus = useAppSelector(selectReprocessFileStatus);
    const error = useAppSelector(selectError);
    const actionError = useAppSelector(selectActionError);

    const isAdmin = viewerIsAdmin(viewer?.role);

    const handleSelectionModelChange = (newModel: GridSelectionModel) => {
        setSelectionModel(newModel);
        const selectedRows = rows.filter(row => newModel.includes(row.id));
        setSelectedFiles(selectedRows);
    };

    const handleSortModelChange = (model: GridSortModel) => {
        if (model.length > 0) {
            setSortModel(model);
        }
    };
    
    const resetDownloadStatus = useCallback(() => {
        setIsDownloading(false);
        setShouldOpenInTab(false);
        dispatch(captureDownloadFileStatus(undefined));
    }, []);

    const handleDownloadOrOpen = useCallback((event: React.MouseEvent, filePath: string, openInTab?: boolean) => {
        event.stopPropagation();
        setIsDownloading(true);
        setShouldOpenInTab(openInTab ?? false);
        dispatch(downloadFile(filePath));
    }, []);

    useEffect(() => {
        if (downloadStatus?.result === RequestResult.Success) {
            if (downloadStatus.response && downloadStatus.response.fileContent && downloadStatus.response.fileName) {
                let mimeType = downloadStatus.response.fileName.toLowerCase().endsWith(".xml") ? "text/xml" : "text/plain";
                downloadDocument(
                    downloadStatus.response.fileContent,
                    downloadStatus.response.fileName,
                    mimeType,
                    true,
                    shouldOpenInTab
                );
                dispatch(setToastConfig({
                    message: shouldOpenInTab ? "File opened successfully" : "File downloaded successfully",
                    severity: ToastSeverity.Success
                }));
            } else {
                dispatch(setToastConfig({
                    message: "File content is empty or not available",
                    severity: ToastSeverity.Error
                }));
            }
            resetDownloadStatus();
        } else if (downloadStatus?.result === RequestResult.Fail) {
            dispatch(setToastConfig({
                message: downloadStatus.message as string,
                severity: ToastSeverity.Error
            }));
            resetDownloadStatus();
        }
    }, [downloadStatus, shouldOpenInTab, resetDownloadStatus]);

    useEffect(() => {
        if (deleteStatus?.result === RequestResult.Success) {
            setOpenDeleteDialog(false);
            dispatch(setToastConfig({
                message: deleteStatus.message as string,
                severity: ToastSeverity.Success
            }));
            dispatch(captureDeleteFileStatus(undefined));
            refreshTab();
        } else if (deleteStatus?.result === RequestResult.Fail) {
            setDeleteErrorMessage(deleteStatus.message as string);
        }
    }, [deleteStatus, refreshTab]);

    useEffect(() => {
        if (moveStatus?.result === RequestResult.Success) {
            setOpenMoveDialog(false);
            dispatch(setToastConfig({
                message: moveStatus.message as string,
                severity: ToastSeverity.Success
            }));
            dispatch(captureMoveFileStatus(undefined));
            refreshTab();
        } else if (moveStatus?.result === RequestResult.Fail) {
            setMoveErrorMessage(moveStatus.message as string);
        }
    }, [moveStatus, refreshTab]);

    useEffect(() => {
        if (reprocessStatus?.result === RequestResult.Success) {
            setOpenReprocessDialog(false);
            setIsReprocessing(false);
            dispatch(setToastConfig({
                message: reprocessStatus.message as string,
                severity: ToastSeverity.Success
            }));
            dispatch(captureReprocessFileStatus(undefined));
            refreshTab();
        } else if (reprocessStatus?.result === RequestResult.Fail) {
            setReprocessErrorMessage(reprocessStatus.message as string);
            setIsReprocessing(false);
        }
    }, [reprocessStatus, refreshTab]);

    useEffect(() => {
        if (deleteDirectoryStatus?.result === RequestResult.Success) {
            setOpenDeleteDialog(false);
            dispatch(setToastConfig({
                message: deleteDirectoryStatus.message as string,
                severity: ToastSeverity.Success
            }));
            dispatch(captureDeleteDirectoryStatus(undefined));
            refreshTab();
            refreshTree?.();
        } else if (deleteDirectoryStatus?.result === RequestResult.Fail) {
            setDeleteErrorMessage(deleteDirectoryStatus.message as string);
        }
    }, [deleteDirectoryStatus, refreshTab, refreshTree]);

    const handleDeleteDirectory = useCallback((event: React.MouseEvent, directoryPath: string) => {
        event.stopPropagation();
        setDeleteErrorMessage('');
        setSelectedDirectoryPath(directoryPath);
        setSelectionModel([]);
        setSelectedFiles([]);
        setOpenDeleteDialog(true);
    }, []);

    const handleDeleteClick = () => {
        setDeleteErrorMessage('');
        setOpenDeleteDialog(true);
    };

    const handleReprocessClick = () => {
        setReprocessErrorMessage('');
        setOpenReprocessDialog(true);
    };

    const onReprocessDialogClose = () => {
        setOpenReprocessDialog(false);
        setIsReprocessing(false);
        setReprocessErrorMessage('');
        dispatch(captureReprocessFileStatus(undefined));
        dispatch(clearActionError());
    };

    const onReprocessDialogConfirm = () => {
        setIsReprocessing(true);
        if (selectedFiles.length > 0) {
            dispatch(reprocessFile(selectedFiles.map(file => file.path)));
        }
    };
    
    const handleMoveClick = () => {
        setMoveErrorMessage('');
        setOpenMoveDialog(true);
    };

    const handleMoveDialogClose = () => {
        setOpenMoveDialog(false);
        setMoveErrorMessage('');
        setSelectionModel([]);
        setSelectedFiles([]);
        dispatch(captureMoveFileStatus(undefined));
        dispatch(clearActionError());
    };

    const handleMove = (destinationPath: string) => {
        if (selectedFiles.length > 0) {
            dispatch(moveFile(selectedFiles.map(file => file.path), destinationPath));
        }
    };
    
    const onDeleteDialogClose = () => {
        setOpenDeleteDialog(false);
        setDeleteErrorMessage('');
        setSelectedDirectoryPath('');
        setSelectionModel([]);
        setSelectedFiles([]);
        dispatch(captureDeleteFileStatus(undefined));
        dispatch(captureDeleteDirectoryStatus(undefined));
        dispatch(clearActionError());
    };

    const onDeleteDialogConfirm = () => {
        if (selectedDirectoryPath) {
            dispatch(deleteDirectory(selectedDirectoryPath));
        } else if (selectedFiles.length > 0) {
            dispatch(deleteFile(selectedFiles.map(file => file.path)));
        }
    };

    const getDialogProps = () => {
        if (selectedDirectoryPath) {
            const dirName = selectedDirectoryPath.split('/').pop() || '';
            return {
                id: dirName,
                heading: "Delete Directory",
                message: `Are you sure you want to delete directory '${dirName}'?`
            };
        }
        
        if (selectedFiles.length === 1) {
            return {
                id: selectedFiles[0].name,
                heading: "Delete File",
                message: `Are you sure you want to delete\n'${selectedFiles[0].name}'?`
            };
        }

        if (selectedFiles.length > 1 && selectedFiles.length <= 5) {
            return {
                id: `${selectedFiles.length} files`,
            heading: "Delete Files",
            message: `Are you sure you want to delete these ${selectedFiles.length} files?\n${selectedFiles.map(f => f.name).join('\n')}`
            };
        }

        return {
            id: `${selectedFiles.length} files`,
            heading: "Delete Files",
            message: `Are you sure you want to delete the ${selectedFiles.length} selected files?`
        };
    };

    const baseColumns = useMemo<GridColumns>(() => [
        {
            headerName: 'CREATED',
            field: 'createdTime',
            flex: 1,
            sortable: true,
            type: 'dateTime',
            renderCell: (params: GridRenderCellParams) => {
                const { value } = params;
                let dateValue = value ? getShortDateString(value) : undefined;
                let timeValue = value ? getTimeString(value, { includeSeconds: true }) : undefined;
                return (
                    <GridCellDualVert header={dateValue} sub={timeValue} />
                );
            }
        },
        {
            headerName: 'LAST MODIFIED',
            field: 'lastModifiedTime',
            flex: 1,
            sortable: true,
            type: 'dateTime',
            renderCell: (params: GridRenderCellParams) => {
                const { value } = params;
                let dateValue = value ? getShortDateString(value) : undefined;
                let timeValue = value ? getTimeString(value, { includeSeconds: true }) : undefined;
                return (
                    <GridCellDualVert header={dateValue} sub={timeValue} />
                );
            }
        },
        {
            headerName: 'SIZE',
            field: 'fileSize',
            minWidth: 80,
            width: 80,
            sortable: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    params.row.fileSizeDisplay
                );
            }
        }             
    ], []);

    const actionColumns = useMemo<GridColumns>(() => [
        {
            field: 'actions',
            type: 'actions',
            headerName: "ACTIONS",
            width: 120,
            headerAlign: 'right' as const,
            align: 'right' as const,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                let row = params.row as FileRow;
                if (row.type === FileSystemType.File) {
                    return (
                        <Box display="flex" gap={1} alignItems="center" justifyContent="flex-end">
                            <Tooltip title="Open in New Tab">
                                <IconButton
                                    onClick={(e) => handleDownloadOrOpen(e, row.path, true)}
                                    size="small"
                                    color="primary"
                                >
                                    <OpenIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                                <IconButton
                                    onClick={(e) => handleDownloadOrOpen(e, row.path)}
                                    size="small"
                                    color="primary"
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                }
                if (isAdmin && params.row.type === FileSystemType.Directory) {
                    // Only show delete button for directories if user is admin
                    return (
                        <Box display="flex" gap={1} alignItems="center" justifyContent="flex-end">
                            <Tooltip title="Delete Directory">
                                <span>
                                <IconButton
                                    onClick={(e) => handleDeleteDirectory(e, row.path)}
                                    size="small"
                                    color="error"
                                    disabled={row.isDeleteDisabled}
                                >
                                    <DeleteIcon />
                                </IconButton>
                                </span> 
                            </Tooltip>
                        </Box>
                    );
                }
                return null;
            }
        }
    ], [handleDownloadOrOpen, handleDeleteDirectory, isAdmin]);

    const checkboxColumn = useMemo(() => 
        checkboxColumnProps ? {
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
            width: checkboxColumnProps.width ?? GRID_CHECKBOX_SELECTION_COL_DEF.width,
            renderCell: checkboxColumnProps.renderCell
        } : GRID_CHECKBOX_SELECTION_COL_DEF
    , [checkboxColumnProps]);

    const allColumns = useMemo(() => {
        const subColumns = checkboxSelection ? [checkboxColumn, ...columns] : columns;
        const combinedColumns = [...subColumns, ...baseColumns];

        // Add the actions column if either showFileActionButtons is true or the user is an admin
        if (showFileActionButtons || isAdmin) {
            combinedColumns.push(...actionColumns);
        }

        return combinedColumns;
    }, [columns, baseColumns, checkboxSelection, checkboxColumn]);

    const loadingOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title={overLayErrorTitle} error={error}></ErrorMessage>
                )}
                {(!error) && (
                    <CircularProgress aria-label={'progress spinner'} key={'file-manager-spinner'} size={42} sx={{ zIndex: 1 }} />
                )}
            </GridOverlay>
        );
    };

    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title={overLayErrorTitle} error={error}></ErrorMessage>
                )}
                {!error && noRowsMessage && noRowsMessage.length > 0 && (
                    <NoRecordsMessage topMargin={6} message={noRowsMessage} />
                )}
                {!error && noResultsMessage && noResultsMessage.length > 0 && (
                    <NoResultsMessage topMargin={6} message={noResultsMessage} />
                )}
            </GridOverlay>
        );
    };
    
    const canDelete = selectedFiles.length > 0;
    const canReprocess = selectedFiles.length > 0 && 
        selectedFiles.every(file => {
            const parentDir = file.path.substring(0, file.path.lastIndexOf('/'));
            return parentDir.endsWith('/Failed') || parentDir.endsWith('/Archive');
        });

    // Search functionality handlers
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onSearchChange) {
            onSearchChange(event.target.value);
        }
    };

    const clearSearch = () => {
        if (onSearchChange) {
            onSearchChange('');
        }
    };

    const handleSearch = () => {
        // Trigger search with current search value (for button clicks and Enter key)
        if (onSearchSubmit) {
            onSearchSubmit(searchValue);
        } else if (onSearchChange) {
            onSearchChange(searchValue);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && searchValue.length >= minSearchLength) {
            handleSearch();
        }
    };

    // Render search component (always enabled)
    const renderSearchComponent = () => {
        return (
            <SearchContainer>
                <SearchInput
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                />
                {searchValue && (
                    <IconButton size="small" onClick={clearSearch}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
                
                <Tooltip title={searchValue.length < minSearchLength ? `Enter at least ${minSearchLength} characters to search` : "Search"}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleSearch}
                            disabled={minSearchLength <= 0 || searchValue.length < minSearchLength}
                            color={searchValue.length >= minSearchLength ? "primary" : "default"}
                            sx={{ mr: 1 }}
                        >
                            <SearchIcon />
                        </IconButton>
                    </span>
                </Tooltip>
              
            </SearchContainer>
        );
    };

    // Render action buttons
    const renderActionButtons = () => {
        return (
            <Box display="flex" gap={1} alignItems="center">
                {customToolbarButtonContent}
                {showFileActionButtons && isAdmin && (
                    <Tooltip title="Move File(s)">
                        <span>
                            <IconButton
                                onClick={handleMoveClick}
                                size="small"
                                color="primary"
                                disabled={!canDelete}
                            >
                                <DriveFileMoveIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {showFileActionButtons && showReprocessButton && isAdmin && (
                    <Tooltip title="Reprocess Files">
                        <span>
                            <IconButton
                                onClick={handleReprocessClick}
                                size="small"
                                color="primary"
                                disabled={!canReprocess}
                            >
                                <ReprocessIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {showFileActionButtons && isAdmin && (
                    <Tooltip title="Delete File(s)">
                        <span>
                            <IconButton
                                onClick={handleDeleteClick}
                                size="small"
                                color="error"
                                disabled={!canDelete}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>
        );
    };

    // Simplified toolbar content - always show search
    const renderToolbarContent = () => {
        return (
            <Box display="flex" flexDirection="column" width="100%" gap={1}>
                {/* Top row: breadcrumbs (if provided) */}
                {customToolbarBreadcrumbsContent && (
                    <Box display="flex" alignItems="center" width="100%">
                        {customToolbarBreadcrumbsContent}
                    </Box>
                )}
                {/* Bottom row: search on left, buttons on right */}
                <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                    {renderSearchComponent()}
                    {renderActionButtons()}
                </Box>
            </Box>
        );
    };

    // Calculate toolbar height dynamically
    const calculateToolbarHeight = () => {
        // Base toolbar height (padding + search component height)
        const baseHeight = 74; // Default height for single row + gap
        
        // If breadcrumbs are present, add extra height for the additional row
        if (customToolbarBreadcrumbsContent) {
            return baseHeight + 14; // Add space for breadcrumbs row
        }
        
        return baseHeight;
    };

    return (
        <TabContainer>
            <TabToolbar justify="space-between" height="auto">
                {renderToolbarContent()}
            </TabToolbar>
            <TabContent toolbarHeight={calculateToolbarHeight()}>
                <FileManagerDataGrid
                    {...gridProps}
                    loading={loading}
                    rows={rows}
                    columns={allColumns}
                    sortingMode="client"
                    sortingOrder={['asc', 'desc']}
                    disableMultipleColumnsSorting
                    disableColumnMenu
                    pagination={checkboxSelection}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    rowsPerPageOptions={[25, 50, 100, 250, 500]}
                    hideFooter={!checkboxSelection}
                    autoHeight={false}
                    headerHeight={38}
                    rowHeight={52}
                    rowBuffer={10}
                    columnBuffer={2}
                    disableVirtualization={false}
                    checkboxSelection={isAdmin && checkboxSelection}
                    selectionModel={selectionModel}
                    onSelectionModelChange={handleSelectionModelChange}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    components={{
                        LoadingOverlay: loadingOverlay,
                        NoRowsOverlay: noRowsOverlay
                    }}
                />
            </TabContent>
            {isDownloading && (
                <CircularProgress
                    aria-label="progress spinner"
                    size={48}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-24px',
                        marginLeft: '-24px',
                        zIndex: 1500
                    }}
                />
            )}
            {openDeleteDialog && (
                <DeleteDialog
                    isOpen={openDeleteDialog}
                    {...getDialogProps()}
                    onConfirm={onDeleteDialogConfirm}
                    onReject={onDeleteDialogClose}
                    errorMessage={deleteErrorMessage}
                    error={actionError}
                />
            )}
            <SelectDirectoryDialog
                isOpen={openMoveDialog}
                // traverse from the first selected file's path to get 1 directory above the current directory as the root parent
                // path.lastIndexOf('/') gets the current directory since after that is the file name, so we need to get directory above it
                // and if we are already at root, then return the full path
                rootParentPath={selectedFiles.length > 0 ? 
                    (() => {
                        const filePath = selectedFiles[0].path;
                        const currentDirPath = filePath.substring(0, filePath.lastIndexOf('/'));
                        const traversedDirPath = currentDirPath.substring(0, currentDirPath.lastIndexOf('/'));
                        if (traversedDirPath.length > 0) {
                            return traversedDirPath; 
                        }
                        return currentDirPath; // already at root
                    })() 
                    : ''}
                onClose={handleMoveDialogClose}
                onMove={handleMove}
                disabledPaths={selectedFiles.map(file => file.path.substring(0, file.path.lastIndexOf('/')))}
                error={actionError}
            />
            <Dialog open={openReprocessDialog}>
                <ConfirmationPrompt 
                    heading="Reprocess Files"
                    message={`Are you sure you want to reprocess ${selectedFiles.length} selected file${selectedFiles.length === 1 ? '' : 's'}?`}
                    handleConfirm={onReprocessDialogConfirm}
                    handleReject={onReprocessDialogClose}
                    isBusy={isReprocessing}
                    errorMessage={reprocessErrorMessage}
                    error={actionError}
                />
            </Dialog>
        </TabContainer>
    );
};

export default FileManagerTabBase;
